import * as React from 'react'

import { Box, CollapsableCard, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/prices'
import { usePricingContext } from 'modules/onboarding/components/Pricing/PricingContext'
import { Tier } from 'modules/onboarding/lib/pricing/types'

type Props = {
  seats: number
  currency: string
}

type OrderItem = {
  title: string
  perSeatPrice?: number
  feePrice?: number
  tieredPrice?: Tier[]
}

const OrderItem = ({
  title,
  perSeatPrice,
  feePrice,
  tieredPrice,
  seats,
  currency,
}: OrderItem & Props) => {
  const price = perSeatPrice ? perSeatPrice * seats : feePrice

  // The first tier of a plan have a price per seat of 0. We can take the endingUnit of this tier to figure out the starting seats.
  const includedSeatsPerModule = tieredPrice?.[0]?.endingUnit
  // The last tier is where we can find the price per seat of all those seats above the "starting seats".
  const pricePerUser = tieredPrice?.[tieredPrice?.length - 1]?.price || 0

  return (
    <Box gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text size="200" color="grey1000" weight="semibold">
          {title}
        </Text>
        <Text size="200" color="grey1000" weight="semibold">
          {formatPrice(price || 0, currency)}
        </Text>
      </Box>
      {perSeatPrice ? (
        <Text size="200" weight="normal">
          {i18n.t('pricing_plans.modules.per_new_user', {
            price: formatPrice(perSeatPrice, currency),
          })}
        </Text>
      ) : null}
      {feePrice ? (
        <Text size="200" weight="normal">
          {i18n.t('pricing_plans.fixed_fee')}
          {includedSeatsPerModule && (
            <>
              {`, ${i18n.t('pricing_plans.employees_inludede', {
                employees: includedSeatsPerModule,
              })}`}
            </>
          )}
        </Text>
      ) : null}
      {tieredPrice ? (
        <Text.Normal size="200">
          {i18n.t('pricing_plans.per_new_employee', {
            amount: formatPrice(pricePerUser / 100, currency),
          })}
        </Text.Normal>
      ) : null}
    </Box>
  )
}

const OrderSummary = ({ seats, currency }: Props) => {
  const { allPlans, period } = usePricingContext()
  const [isOpen, setIsOpen] = React.useState(false)

  const getOrderSummary = (): OrderItem[] => {
    return allPlans.corePlans
      .concat(allPlans.normalPlans)
      .filter((plan) => plan.isSelected)
      .map((plan) => {
        return {
          title: plan.title,
          perSeatPrice: plan.pricing.getSeatsPrice(period),
          feePrice: plan.pricing.getFeePrice(period),
          tieredPrice: plan.pricing.getTieredPrice(period),
        }
      })
  }

  return (
    <CollapsableCard
      Card={
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          onClick={() => {
            setIsOpen(!isOpen)
          }}
          cursor="pointer"
        >
          <Text size="300" weight="semibold" color="grey1000">
            {i18n.t('pricing_plans.custom_bundles.order_summary')}
          </Text>
          <Icon.Medium icon={isOpen ? 'chevron-up' : 'chevron-down'} />
        </Box>
      }
      Content={
        <Box gap="s24">
          {getOrderSummary().map((orderItem) => (
            <OrderItem key={orderItem.title} {...orderItem} seats={seats} currency={currency} />
          ))}
        </Box>
      }
      open={isOpen}
    />
  )
}

export default OrderSummary
